import { useState } from 'react';

import type { AuditEntryResponse, CompanyResponse, FieldChangeResponse } from '../../api';
import {
  AsyncSection,
  DataTable,
  Form,
  PageHeader,
  Panel,
  SelectField,
  SubmitButton,
  TextField,
  type Column,
} from '../../components';
import { formatDateTime, formatInteger } from '../../format';
import {
  useAuditEntries,
  useCompanies,
  NO_AUDIT_FILTERS,
  type AuditEntryFilters,
} from '../queries';
import {
  ACTION_LABELS,
  ACTION_OPTIONS,
  AUDIT_COPY,
  CHANNEL_LABELS,
  PRINCIPAL_LABELS,
  asInstant,
  formatRecordedValue,
} from './auditTrail';

/**
 * The trail of #208, read.
 *
 * Nothing on this screen writes anything, and there is no endpoint that could:
 * an audit entry is a consequence of doing something else, and one a caller
 * could ask for would be an assertion rather than a record. So this is a filter
 * panel over a page of a table, and that is all it will ever be.
 *
 * Three decisions worth stating.
 *
 * **The filters are in the component, not in the URL.** `routes/paths.ts`
 * forbids identifying data in an address, and every filter here is identifying:
 * the record being investigated, the person who changed it, the request that
 * carried it. A shareable link would be a CPF's worth of investigation in
 * somebody's browser history. The cost is that a filtered view cannot be sent
 * to a colleague, and that is the right way round -- the colleague can open the
 * screen and filter it themselves, and their read is recorded as theirs.
 *
 * **They apply on submit rather than on every keystroke.** Six fields that each
 * fire a request as they are typed is six decryptions per character on an
 * endpoint that says in its own documentation that decryption is why its page
 * ceiling is low.
 *
 * **The values live in the expanded entry, not in the grid.** `changes` carries
 * decrypted personal data -- salaries, documents, dates of birth. Showing it is
 * exactly what `AUDIT_READ` is for, and showing it forty rows at a time on a
 * screen somebody has open in a meeting is not. Opening one entry is a
 * deliberate act, and the screen says what it costs above the table.
 */
export function AuditTrailPage() {
  // Two copies of the filter on purpose: what is typed, and what is being
  // asked. Without the second, every keystroke would be a new query key.
  const [draft, setDraft] = useState<AuditEntryFilters>(NO_AUDIT_FILTERS);
  const [applied, setApplied] = useState<AuditEntryFilters>(NO_AUDIT_FILTERS);
  const [openEntryId, setOpenEntryId] = useState<string | null>(null);

  const entries = useAuditEntries(applied);
  const companies = useCompanies();

  const change = (patch: Partial<AuditEntryFilters>) => setDraft({ ...draft, ...patch });

  /** Any change of filter starts the paging again: page three of the old answer is not page three of the new one. */
  const apply = (next: AuditEntryFilters) => {
    const fromTheTop = { ...next, offset: 0 };
    setDraft(fromTheTop);
    setApplied(fromTheTop);
    setOpenEntryId(null);
  };

  const companyName = (companyId: string | null | undefined) => {
    if (companyId === null || companyId === undefined) {
      return null;
    }
    const known = companies.data?.items.find(
      (company: CompanyResponse) => company.id === companyId,
    );
    return known?.name ?? companyId;
  };

  const columns: readonly Column<AuditEntryResponse>[] = [
    { key: 'when', header: 'Quando', render: (row) => formatDateTime(row.occurred_at) },
    { key: 'action', header: 'Ação', render: (row) => ACTION_LABELS[row.action] ?? row.action },
    {
      key: 'entity',
      header: 'Registro',
      render: (row) => (
        <>
          {row.entity_type}
          <br />
          <code className="table__muted">{row.entity_id}</code>
        </>
      ),
    },
    {
      key: 'actor',
      header: 'Quem fez',
      render: (row) => (
        <>
          {PRINCIPAL_LABELS[row.actor_type] ?? row.actor_type}
          <br />
          <code className="table__muted">{row.actor_id}</code>
        </>
      ),
    },
    {
      key: 'channel',
      header: 'Canal',
      render: (row) => CHANNEL_LABELS[row.channel] ?? row.channel,
    },
    {
      key: 'company',
      header: 'Empresa',
      render: (row) => companyName(row.company_id) ?? <Muted>{AUDIT_COPY.outsideCompany}</Muted>,
    },
    {
      key: 'detail',
      header: 'Detalhes',
      align: 'end',
      render: (row) => (
        <button
          type="button"
          className="button button--secondary"
          aria-expanded={openEntryId === row.id}
          onClick={() => setOpenEntryId(openEntryId === row.id ? null : row.id)}
        >
          {openEntryId === row.id ? AUDIT_COPY.collapse : AUDIT_COPY.expand}
        </button>
      ),
    },
  ];

  return (
    <div className="page">
      <PageHeader title={AUDIT_COPY.title} description={AUDIT_COPY.description} />

      <Panel title={AUDIT_COPY.filtersTitle} description={AUDIT_COPY.filtersDescription}>
        <Form onSubmit={() => apply(draft)}>
          <SelectField
            label={AUDIT_COPY.action}
            value={draft.action}
            onChange={(action) => change({ action })}
            options={ACTION_OPTIONS}
            placeholder={AUDIT_COPY.anyAction}
          />
          <SelectField
            label={AUDIT_COPY.company}
            value={draft.companyId}
            onChange={(companyId) => change({ companyId })}
            options={(companies.data?.items ?? []).map((company: CompanyResponse) => ({
              value: company.id,
              label: company.name,
            }))}
            placeholder={AUDIT_COPY.anyCompany}
            hint={AUDIT_COPY.companyHint}
          />
          <TextField
            label={AUDIT_COPY.entityType}
            hint={AUDIT_COPY.entityTypeHint}
            value={draft.entityType}
            onChange={(entityType) => change({ entityType })}
          />
          <TextField
            label={AUDIT_COPY.entityId}
            hint={AUDIT_COPY.entityIdHint}
            value={draft.entityId}
            onChange={(entityId) => change({ entityId })}
          />
          <TextField
            label={AUDIT_COPY.actorId}
            hint={AUDIT_COPY.actorIdHint}
            value={draft.actorId}
            onChange={(actorId) => change({ actorId })}
          />
          <TextField
            label={AUDIT_COPY.correlationId}
            hint={AUDIT_COPY.correlationIdHint}
            value={draft.correlationId}
            onChange={(correlationId) => change({ correlationId })}
          />
          <TextField
            label={AUDIT_COPY.occurredFrom}
            hint={AUDIT_COPY.occurredFromHint}
            type="date"
            value={dayOf(draft.occurredFrom)}
            onChange={(day) => change({ occurredFrom: asInstant(day) })}
          />
          <TextField
            label={AUDIT_COPY.occurredTo}
            hint={AUDIT_COPY.occurredToHint}
            type="date"
            value={dayOf(draft.occurredTo)}
            onChange={(day) => change({ occurredTo: asInstant(day) })}
          />
          <div className="form__actions">
            <SubmitButton pending={entries.isFetching} pendingLabel="Buscando…">
              {AUDIT_COPY.apply}
            </SubmitButton>
            <button
              type="button"
              className="button button--ghost"
              onClick={() => apply(NO_AUDIT_FILTERS)}
            >
              {AUDIT_COPY.clear}
            </button>
          </div>
        </Form>
      </Panel>

      <Panel title={AUDIT_COPY.entriesTitle} description={AUDIT_COPY.entriesDescription}>
        <p className="panel__footnote panel__footnote--warning">{AUDIT_COPY.personalData}</p>

        <AsyncSection
          state={entries}
          loadingLabel={AUDIT_COPY.loading}
          errorTitle={AUDIT_COPY.loadError}
        >
          {(data) => (
            <>
              <DataTable
                id="audit-trail"
                caption="Entradas da trilha de auditoria"
                columns={columns}
                rows={data.items}
                rowKey={(row) => row.id}
                emptyTitle={AUDIT_COPY.noEntries}
                emptyDescription={AUDIT_COPY.noEntriesHint}
                expansion={(row) =>
                  openEntryId === row.id ? (
                    <EntryDetail
                      entry={row}
                      onFilterByCorrelation={() =>
                        apply({ ...applied, correlationId: row.correlation_id })
                      }
                    />
                  ) : null
                }
              />
              <div className="form__actions">
                <button
                  type="button"
                  className="button button--secondary"
                  disabled={applied.offset === 0}
                  onClick={() => {
                    setApplied({
                      ...applied,
                      offset: Math.max(0, applied.offset - data.limit),
                    });
                    setOpenEntryId(null);
                  }}
                >
                  {AUDIT_COPY.previous}
                </button>
                <button
                  type="button"
                  className="button button--secondary"
                  // The page the server actually applied, not one this screen
                  // chose: a short page is the last one.
                  disabled={data.count < data.limit}
                  onClick={() => {
                    setApplied({ ...applied, offset: data.offset + data.limit });
                    setOpenEntryId(null);
                  }}
                >
                  {AUDIT_COPY.next}
                </button>
              </div>
              {data.count > 0 && (
                <p className="panel__footnote">
                  {`Mostrando ${formatInteger(data.offset + 1)}–${formatInteger(
                    data.offset + data.count,
                  )}, ${formatInteger(data.limit)} por página.`}
                </p>
              )}
            </>
          )}
        </AsyncSection>
      </Panel>
    </div>
  );
}

/**
 * One entry, opened: where the request came from, which request it was, and
 * every field it touched.
 *
 * The correlation id is a button rather than text because "everything this one
 * request did" is the question an investigation actually asks, and typing a
 * uuid into a filter by hand is how it gets asked wrong.
 */
function EntryDetail({
  entry,
  onFilterByCorrelation,
}: {
  entry: AuditEntryResponse;
  onFilterByCorrelation: () => void;
}) {
  const fields = Object.entries(entry.changes);

  return (
    <div className="audit-detail">
      <dl className="definitions">
        <div>
          <dt>{AUDIT_COPY.clientAddress}</dt>
          <dd>
            {entry.client_address != null && entry.client_address !== '' ? (
              <code>{entry.client_address}</code>
            ) : (
              <Muted>{AUDIT_COPY.noClientAddress}</Muted>
            )}
          </dd>
        </div>
        <div>
          <dt>{AUDIT_COPY.correlationId}</dt>
          <dd>
            <button
              type="button"
              className="button button--ghost"
              aria-label={`${AUDIT_COPY.filterByCorrelation}: ${entry.correlation_id}`}
              onClick={onFilterByCorrelation}
            >
              <code>{entry.correlation_id}</code>
            </button>
            <span className="table__muted"> {AUDIT_COPY.correlationHelp}</span>
          </dd>
        </div>
      </dl>

      <h3 className="panel__subtitle">{AUDIT_COPY.detailTitle}</h3>
      {fields.length === 0 ? (
        <p className="panel__footnote">{AUDIT_COPY.noChanges}</p>
      ) : (
        <dl className="definitions">
          {fields.map(([field, values]) => (
            <div key={field}>
              <dt>{field}</dt>
              <dd>
                <RecordedValue label={AUDIT_COPY.before} change={values} end="before" />
                {' → '}
                <RecordedValue label={AUDIT_COPY.after} change={values} end="after" />
              </dd>
            </div>
          ))}
        </dl>
      )}
    </div>
  );
}

/**
 * One end of one change.
 *
 * `null` is not "empty" here: the contract says `before: null` means the record
 * did not exist and `after: null` means it ceased to, and a creation rendered
 * as a blank space beside an arrow is a creation nobody can tell apart from a
 * field that was cleared.
 */
function RecordedValue({
  label,
  change,
  end,
}: {
  label: string;
  change: FieldChangeResponse;
  end: 'before' | 'after';
}) {
  const value = end === 'before' ? change.before : change.after;
  const absent = end === 'before' ? AUDIT_COPY.didNotExist : AUDIT_COPY.ceasedToExist;

  if (value === null || value === undefined) {
    return (
      <span className="table__muted">
        {label}: {absent}
      </span>
    );
  }
  return (
    <span>
      {label}: <code>{formatRecordedValue(value)}</code>
    </span>
  );
}

/** The day inside an instant the filter holds, for the date field to show. */
function dayOf(instant: string): string {
  return instant.slice(0, 10);
}

function Muted({ children }: { children: React.ReactNode }) {
  return <span className="table__muted">{children}</span>;
}
